import React from 'react';
import { motion } from 'framer-motion';
import {
  NewspaperIcon,
  ArrowDownTrayIcon,
  PhotoIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  LinkIcon,
  EnvelopeIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const PressKitPage: React.FC = () => {
  const pressReleases = [
    {
      date: 'January 15, 2026',
      title: 'TEMPLUM Launches Revolutionary Decentralized Auction Platform on Polygon',
      summary: 'NARDIHA Holdings announces the mainnet launch of TEMPLUM, offering four unique auction mechanisms for digital and tokenized assets.'
    },
    {
      date: 'December 1, 2025',
      title: 'TEMPLUM Completes $10M Strategic Investment Round',
      summary: 'Leading Web3 investors join NARDIHA ecosystem to accelerate development of decentralized commerce infrastructure.'
    },
    {
      date: 'October 15, 2025',
      title: 'TEMPLUM Smart Contracts Pass Security Audit by CertiK',
      summary: 'Independent security audit confirms the integrity and safety of TEMPLUM auction protocols.'
    }
  ];

  const mediaAssets = [
    {
      category: 'Logos',
      items: [
        { name: 'TEMPLUM Logo - Primary', format: 'SVG, PNG', size: '2.4 MB' },
        { name: 'TEMPLUM Logo - White', format: 'SVG, PNG', size: '2.1 MB' },
        { name: 'TEMPLUM Logo - Dark', format: 'SVG, PNG', size: '2.1 MB' },
        { name: 'TEMPLUM Wordmark', format: 'SVG, PNG', size: '1.8 MB' },
      ]
    },
    {
      category: 'Brand Assets',
      items: [
        { name: 'Brand Guidelines', format: 'PDF', size: '8.5 MB' },
        { name: 'Color Palette', format: 'PNG, ASE', size: '0.5 MB' },
        { name: 'Typography Guide', format: 'PDF', size: '2.2 MB' },
        { name: 'Icon Set', format: 'SVG, PNG', size: '4.1 MB' },
      ]
    },
    {
      category: 'Product Screenshots',
      items: [
        { name: 'Auction Interface', format: 'PNG', size: '3.2 MB' },
        { name: 'Dashboard View', format: 'PNG', size: '2.8 MB' },
        { name: 'Bidding Flow', format: 'PNG', size: '2.5 MB' },
        { name: 'Mobile App Screens', format: 'PNG', size: '5.1 MB' },
      ]
    },
    {
      category: 'Videos',
      items: [
        { name: 'Platform Overview', format: 'MP4', size: '45 MB' },
        { name: 'How to Bid - Tutorial', format: 'MP4', size: '28 MB' },
        { name: 'Brand Intro Animation', format: 'MP4', size: '12 MB' },
      ]
    }
  ];

  const mediaContacts = [
    {
      name: 'Press Inquiries',
      email: 'press@nardiha.com',
      response: '24-48 hours'
    },
    {
      name: 'Interview Requests',
      email: 'media@nardiha.com',
      response: '48-72 hours'
    }
  ];

  const factSheet = {
    company: 'NARDIHA Holdings S.L.',
    platform: 'TEMPLUM',
    founded: '2023',
    headquarters: 'Barcelona, Spain',
    network: 'Polygon (Ethereum L2)',
    auctionTypes: ['English', 'Dutch', 'Vickrey', 'Candle'],
    token: 'NDH (NARDIHA Token)',
    website: 'templum.io'
  };

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <NewspaperIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Press <span className="text-primary-400">Kit</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Everything journalists and content creators need to cover TEMPLUM and the NARDIHA ecosystem.
            </p>
            <a
              href="/press-kit.zip"
              download
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Download Full Press Kit (125 MB)
            </a>
          </motion.div>
        </div>
      </section>

      {/* Fact Sheet */}
      <section className="py-16 bg-dark-900/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Quick Facts</h2>
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex justify-between py-3 border-b border-dark-600">
                  <span className="text-gray-400">Company</span>
                  <span className="text-white">{factSheet.company}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-dark-600">
                  <span className="text-gray-400">Platform</span>
                  <span className="text-white">{factSheet.platform}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-dark-600">
                  <span className="text-gray-400">Founded</span>
                  <span className="text-white">{factSheet.founded}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-dark-600">
                  <span className="text-gray-400">Headquarters</span>
                  <span className="text-white">{factSheet.headquarters}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-dark-600">
                  <span className="text-gray-400">Blockchain</span>
                  <span className="text-white">{factSheet.network}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-dark-600">
                  <span className="text-gray-400">Token</span>
                  <span className="text-white">{factSheet.token}</span>
                </div>
                <div className="flex justify-between py-3 md:col-span-2">
                  <span className="text-gray-400">Auction Types</span>
                  <span className="text-white">{factSheet.auctionTypes.join(', ')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Press Releases */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Press Releases</h2>
            <div className="space-y-4">
              {pressReleases.map((release, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 hover:border-primary-500/50 transition-colors"
                >
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <CalendarIcon className="w-4 h-4" />
                    {release.date}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{release.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{release.summary}</p>
                  <a href="#" className="text-primary-400 text-sm hover:underline">
                    Read Full Release →
                  </a>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Media Assets */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">Media Assets</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {mediaAssets.map((category, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
                >
                  <div className="flex items-center gap-3 mb-4">
                    {category.category === 'Logos' && <PhotoIcon className="w-6 h-6 text-primary-400" />}
                    {category.category === 'Brand Assets' && <DocumentTextIcon className="w-6 h-6 text-blue-400" />}
                    {category.category === 'Product Screenshots' && <PhotoIcon className="w-6 h-6 text-green-400" />}
                    {category.category === 'Videos' && <VideoCameraIcon className="w-6 h-6 text-purple-400" />}
                    <h3 className="text-lg font-semibold text-white">{category.category}</h3>
                  </div>
                  <div className="space-y-3">
                    {category.items.map((item, iidx) => (
                      <div key={iidx} className="flex items-center justify-between p-3 bg-dark-900/50 rounded-lg">
                        <div>
                          <p className="text-white text-sm">{item.name}</p>
                          <p className="text-gray-500 text-xs">{item.format} • {item.size}</p>
                        </div>
                        <button className="p-2 text-primary-400 hover:bg-primary-500/20 rounded-lg transition-colors">
                          <ArrowDownTrayIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Boilerplate */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">About TEMPLUM (Boilerplate)</h2>
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <p className="text-gray-300 mb-4">
                TEMPLUM is a decentralized auction platform built on Polygon blockchain, offering four distinct auction mechanisms (English, Dutch, Vickrey, and Candle) for optimal price discovery across digital and real-world tokenized assets.
              </p>
              <p className="text-gray-300 mb-4">
                As Platform #20 in the NARDIHA Holdings ecosystem, TEMPLUM leverages the NDH token economy to provide users with staking rewards, governance rights, and reduced fees. The platform features DAO governance, allowing token holders to shape the future of the protocol.
              </p>
              <p className="text-gray-300">
                Founded in 2023 and headquartered in Barcelona, Spain, NARDIHA Holdings is building a comprehensive blockchain ecosystem with over 50 interconnected platforms serving users in 180+ countries worldwide.
              </p>
              <button className="mt-4 flex items-center gap-2 text-primary-400 hover:text-primary-300">
                <LinkIcon className="w-4 h-4" />
                Copy Boilerplate
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Media Contact */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Media Contact</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {mediaContacts.map((contact, idx) => (
                <div key={idx} className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
                  <h3 className="text-lg font-semibold text-white mb-3">{contact.name}</h3>
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-2 text-primary-400 mb-2 hover:underline"
                  >
                    <EnvelopeIcon className="w-5 h-5" />
                    {contact.email}
                  </a>
                  <p className="text-gray-500 text-sm">Response time: {contact.response}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guidelines */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Usage Guidelines</h2>
            <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700">
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-green-400 rounded-full mt-2"></span>
                  Use official logos and assets without modification
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-green-400 rounded-full mt-2"></span>
                  Maintain adequate clear space around logos
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-green-400 rounded-full mt-2"></span>
                  Use the correct spelling: "TEMPLUM" (all caps) and "NARDIHA"
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-red-400 rounded-full mt-2"></span>
                  Do not alter colors or proportions
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-2 h-2 bg-red-400 rounded-full mt-2"></span>
                  Do not use logos on busy or clashing backgrounds
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PressKitPage;
